/**
 * CSP-safe RJSF ValidatorType implementation backed by @cfworker/json-schema.
 *
 * AJV (used by @rjsf/validator-ajv8) generates and executes JavaScript code at
 * runtime via `new Function()`, which requires `unsafe-eval` in the Content
 * Security Policy. @cfworker/json-schema interprets schemas without any code
 * generation, so it works under a strict CSP.
 *
 * This module implements the RJSF `ValidatorType` interface:
 *   - validateFormData(formData, schema, customValidate?, transformErrors?, uiSchema?)
 *   - isValid(schema, formData, rootSchema)
 *   - rawValidation(schema, formData?)
 *   - toErrorList(errorSchema?, fieldPath?)
 *
 * Usage (replaces customizeValidator in JsonEditor.js):
 *   import createCspSafeValidator from './cspSafeValidator';
 *   const validator = createCspSafeValidator(customFormats);
 */

import { Validator, format as cfworkerFormat } from '@cfworker/json-schema';
import {
  createErrorHandler,
  getDefaultFormState,
  toErrorList,
  toErrorSchema,
  unwrapErrorHandler,
  validationDataMerge,
} from '@rjsf/utils';

// Keywords that @cfworker emits as parent-level containers for child errors.
// They duplicate information already present in child errors, so we drop them
// to keep the RJSF error list clean.
const CONTAINER_KEYWORDS = new Set(['properties', 'items', 'allOf', 'anyOf', 'oneOf', 'if']);

/**
 * Register custom formats into the @cfworker shared format registry.
 * The registry is a plain object referenced directly by the validator, so
 * mutating it affects all subsequent Validator instances.
 *
 * @param {Record<string, RegExp|function>} customFormats
 */
function registerFormats(customFormats) {
  for (const [name, validator] of Object.entries(customFormats)) {
    // Guard against prototype pollution via crafted format names.
    if (name === '__proto__' || name === 'constructor' || name === 'prototype') continue;
    if (typeof validator === 'function') {
      cfworkerFormat[name] = validator;
    } else if (validator instanceof RegExp) {
      const re = validator;
      cfworkerFormat[name] = (value) => re.test(value);
    }
  }
}

/**
 * Rewrite @cfworker error messages to match AJV-style wording.
 * @cfworker messages are verbose ("Instance type "null" is invalid. Expected "string".");
 * AJV messages are terse ("must be string"). Normalising here keeps the UI copy consistent
 * regardless of which validation backend is in use.
 *
 * @param {string} keyword  JSON Schema keyword that triggered the error
 * @param {string} raw      Raw message string from @cfworker
 * @returns {string}
 */
function normalizeMessage(keyword, raw, parentSchema) {
  switch (keyword) {
    case 'type': {
      // "Instance type "null" is invalid. Expected "string"."
      // "Instance type "null" is invalid. Expected "string", "number"."
      const m = raw.match(/Expected "(.+?)"\./);
      return m ? `must be ${m[1]}` : raw;
    }
    case 'required': {
      // "Instance does not have required property "name"."
      const m = raw.match(/required property "([^"]+)"/);
      if (!m) return raw;
      if (m[1] === 'sourceId') return 'Source is required.';
      return `${m[1]} is required.`;
    }
    case 'minLength': {
      // "String is too short (3 < 5)."
      const m = raw.match(/< (\d+)/);
      return m ? `must NOT have fewer than ${m[1]} characters` : raw;
    }
    case 'maxLength': {
      // "String is too long (10 > 5)."
      const m = raw.match(/> (\d+)/);
      return m ? `must NOT have more than ${m[1]} characters` : raw;
    }
    case 'minimum': {
      // "5 is less than  10."
      const m = raw.match(/(?:less than).*?([\d.]+)\.?\s*$/);
      if (m && parentSchema?.maximum !== undefined) {
        return `Value must be >= ${m[1]} and <= ${parentSchema.maximum}`;
      }
      return m ? `must be >= ${m[1]}` : raw;
    }
    case 'maximum': {
      // "5 is greater than  10."
      const m = raw.match(/(?:greater than).*?([\d.]+)\.?\s*$/);
      if (m && parentSchema?.minimum !== undefined) {
        return `Value must be >= ${parentSchema.minimum} and <= ${m[1]}`;
      }
      return m ? `must be <= ${m[1]}` : raw;
    }
    case 'minItems': {
      // "Array has too few items (2 < 3)."
      const m = raw.match(/< (\d+)/);
      return m ? `must NOT have fewer than ${m[1]} items` : raw;
    }
    case 'maxItems': {
      // "Array has too many items (5 > 3)."
      const m = raw.match(/> (\d+)/);
      return m ? `must NOT have more than ${m[1]} items` : raw;
    }
    case 'enum': {
      return 'must be equal to one of the allowed values';
    }
    case 'const': {
      return 'must be equal to the expected value';
    }
    case 'format': {
      // "String does not match format "email"."
      const m = raw.match(/format "([^"]+)"/);
      return m ? `must match format "${m[1]}"` : raw;
    }
    case 'uniqueItems': {
      return 'must NOT have duplicate items';
    }
    case 'multipleOf': {
      // "5 is not a multiple of 3."
      const m = raw.match(/multiple of ([\d.]+)/);
      return m ? `must be a multiple of ${m[1]}` : raw;
    }
    default: {
      return raw;
    }
  }
}

function getParentSchema(schema, keywordLocation) {
  const parts = keywordLocation.replace(/^#\//, '').split('/');
  parts.pop(); // remove the keyword itself, keep the parent path
  let node = schema;
  for (const part of parts) {
    if (node && typeof node === 'object') node = node[part];
    else return null;
  }
  return node;
}

/**
 * Convert a @cfworker error object to the RJSF `RJSFValidationError` shape.
 *
 * @cfworker error shape:
 *   { instanceLocation: '#/fieldName', keyword: 'minLength',
 *     keywordLocation: '#/properties/fieldName/minLength', error: 'String is too short.' }
 *
 * RJSF error shape:
 *   { name, property, message, params, stack, schemaPath }
 */
function cfworkerErrorToRJSF(cfError, schema) {
  const { instanceLocation, keyword, keywordLocation, error: rawMessage } = cfError;

  // Convert '#/foo/bar' -> '.foo.bar'  (RJSF uses dot notation)
  let property =
    instanceLocation === '#' ? '.' : instanceLocation.replace(/^#/, '').replaceAll('/', '.');

  let params = {};

  if (keyword === 'required') {
    // @cfworker places required errors on the parent object.
    // Extract the missing field name from the raw message so RJSF can
    // attach the error to the correct child field.
    const match = rawMessage.match(/required property "([^"]+)"/);
    if (match) {
      const missingProperty = match[1];
      property = property === '.' ? missingProperty : `${property}.${missingProperty}`;
      params = { missingProperty };
    }
  }

  const parentSchema = schema ? getParentSchema(schema, keywordLocation) : null;
  const message = normalizeMessage(keyword, rawMessage, parentSchema);
  const stack = property === '.' ? message : `${property} ${message}`;

  return {
    name: keyword,
    property,
    message,
    params,
    stack,
    schemaPath: keywordLocation,
  };
}

/**
 * @cfworker does not support `undefined` as an instance type — unlike AJV,
 * which silently ignores undefined object properties. RJSF passes partial
 * form data (with undefined fields) when calling isValid() for branch
 * resolution in oneOf/anyOf/dependencies. A JSON round-trip strips all
 * undefined values, matching AJV's behaviour.
 */
function sanitize(data) {
  if (data === undefined) return null;
  try {
    return JSON.parse(JSON.stringify(data));
  } catch {
    return data;
  }
}

/**
 * Run @cfworker validation and return RJSF-shaped errors.
 * Returns `{ errors, validationError }` mirroring the AJV rawValidation shape.
 * @cfworker's `shortCircuit` option defaults to `true`, which stops
 * validation after the first error is found instead of collecting every
 * violation. That caused only one invalid field to be reported per submit.
 * Passing `false` here makes it collect all errors across all fields in a
 * single pass, so every invalid field is flagged at once.
 */
function runValidation(schema, formData) {
  try {
    const shortCircuit = false;
    const v = new Validator(schema, '2019-09', shortCircuit);
    const result = v.validate(sanitize(formData));
    if (result.valid) return { errors: [] };

    const seen = new Set();
    const errors = result.errors
      .filter((error) => !CONTAINER_KEYWORDS.has(error.keyword))
      .map((error) => cfworkerErrorToRJSF(error, schema))
      .filter((error) => {
        const key = `${error.property}|${error.message}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

    return { errors };
  } catch (error) {
    return { errors: [], validationError: error };
  }
}

/**
 * Factory that returns an RJSF ValidatorType implementation.
 * Call once per JsonEditor render, passing in any custom formats for that form.
 *
 * @param {Record<string, RegExp|function>} customFormats
 * @returns {import('@rjsf/utils').ValidatorType}
 */
function createCspSafeValidator(customFormats = {}) {
  registerFormats(customFormats);

  const validatorInstance = {
    // Required by ValidatorType interface — RJSF internals call this via mergeValidationData.
    toErrorList(errorSchema, fieldPath = []) {
      return toErrorList(errorSchema, fieldPath);
    },

    /**
     * Used by RJSF internally to resolve oneOf/anyOf branch selection and
     * conditional schema rendering. Must return a boolean without throwing.
     */
    isValid(schema, formData) {
      try {
        const v = new Validator(schema, '2019-09');
        return v.validate(sanitize(formData)).valid;
      } catch {
        return false;
      }
    },

    /**
     * Low-level validation — returns the raw error list before any RJSF
     * post-processing. Used by processRawValidationErrors and occasionally
     * called directly by RJSF internals.
     */
    rawValidation(schema, formData) {
      return runValidation(schema, formData);
    },

    /**
     * Full RJSF validation pipeline:
     *   1. Run @cfworker schema validation
     *   2. Apply transformErrors (if provided)
     *   3. Build errorSchema
     *   4. Run customValidate (if provided) and merge results
     */
    validateFormData(formData, schema, customValidate, transformErrors, uiSchema) {
      // Apply schema defaults before validating, matching @rjsf/validator-ajv8 behaviour.
      // Without this, fields that have a schema default but haven't been touched by the user
      // are absent from formData, causing spurious required/type errors on first save.
      const resolvedFormData = getDefaultFormState(
        validatorInstance,
        schema,
        formData,
        schema,
        true
      );

      const { errors: rawErrors, validationError } = runValidation(schema, resolvedFormData);

      let errors = validationError ? [{ stack: validationError.message }] : rawErrors;

      if (typeof transformErrors === 'function') {
        errors = transformErrors(errors, uiSchema);
      }

      let errorSchema = toErrorSchema(errors);

      if (validationError) {
        errorSchema = {
          ...errorSchema,
          $schema: { __errors: [validationError.message] },
        };
      }

      if (typeof customValidate !== 'function') {
        return { errors, errorSchema };
      }

      // RJSF customValidate receives form data with defaults applied and a
      // proxy error handler. The handler is unwrapped and merged back in.
      const errorHandler = customValidate(
        resolvedFormData,
        createErrorHandler(resolvedFormData),
        uiSchema
      );
      const userErrorSchema = unwrapErrorHandler(errorHandler);
      return validationDataMerge({ errors, errorSchema }, userErrorSchema);
    },
  };

  return validatorInstance;
}

export default createCspSafeValidator;
